import React, { createContext, useContext, useEffect, useState } from "react";
import { api, ADMIN_TOKEN_STORAGE_KEY } from "../lib/api";

const AuthContext = createContext(null);
const DEVICE_TOKEN_KEY = "velcor_admin_device_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * Step 1: email + password.
   * Returns either:
   *   { requiresTwoFA: true, challengeToken }    → caller must call verifyTwoFA(...)
   *   { requiresTwoFA: false, user, mustSetup2FA }
   *
   * If a device_token from a prior "trust this device" exists in localStorage,
   * it is sent along — server may return access_token directly and skip 2FA
   * when bindings (UA + IP-prefix) match.
   */
  const login = async (email, password) => {
    const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY) || null;
    const { data } = await api.post("/auth/login", {
      email,
      password,
      device_token: deviceToken,
    });
    if (data.requires_2fa) {
      return { requiresTwoFA: true, challengeToken: data.challenge_token };
    }
    // Trusted device skip OR no-2FA user — full access in one step.
    localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, data.access_token);
    setUser(data.user);
    return {
      requiresTwoFA: false,
      user: data.user,
      mustSetup2FA: !!data.must_setup_2fa,
      trustedDevice: !!data.trusted_device,
    };
  };

  /** Step 2: TOTP / backup-code verification after challenge.
   * `trustDevice` (default true) asks the server for a 7-day device_token —
   * stored in localStorage so the next login skips 2FA. */
  const verifyTwoFA = async (challengeToken, code, isBackup = false, trustDevice = true) => {
    const { data } = await api.post("/auth/login/2fa", {
      challenge_token: challengeToken,
      code,
      is_backup: isBackup,
      trust_device: trustDevice,
    });
    localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, data.access_token);
    if (data.device_token) {
      localStorage.setItem(DEVICE_TOKEN_KEY, data.device_token);
    } else if (!trustDevice) {
      // Operator opted out — clear any older device token on this browser
      localStorage.removeItem(DEVICE_TOKEN_KEY);
    }
    setUser(data.user);
    return data.user;
  };

  /** Re-fetch the current user (e.g., after enabling 2FA). */
  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  const logout = () => {
    // We keep the device_token on logout — that's the whole point of
    // "trust this device": next login from the same browser still skips 2FA.
    // The user can revoke devices from /admin/security if they want.
    localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyTwoFA, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
